/**
 * PageExtractorConfig Component
 *
 * Form-based configuration editor for page extractor automation.
 * Provides fields for URLs and elements to extract.
 *
 * @param {Object} props
 * @param {Object} props.config - Current page extractor configuration
 * @param {Function} props.onChange - Called with updated config
 */

import React, { useState } from "react";
import { FiPlus, FiX, FiChevronDown, FiChevronRight, FiLink, FiBox } from "react-icons/fi";
import SelectorArrayEditor from "./SelectorArrayEditor";

// Page Extractor 2.0 action steps — display-only metadata (editing options is out of scope).
const ACTION_META = {
  json_ld: { emoji: "🧩", label: "Automatic Extraction" },
  page_metadata: { emoji: "🏷️", label: "Page Metadata" },
  google_maps: { emoji: "🗺️", label: "Google Maps" },
  phone: { emoji: "📞", label: "Phone Numbers" },
};

/** "published_date" → "Published Date". Keeps this view free of a duplicated field registry. */
const humanizeFieldId = (id) =>
  String(id)
    .replace(/^_+/, "")
    .replace(/__json$/, "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/**
 * One-line summary of a step's options — today only page_metadata has any (its field selection,
 * chosen in the extension). Returns "" when the step runs with its defaults.
 */
const describeStepOptions = (step) => {
  const fields = step?.options?.fields;
  if (step?.actionId !== "page_metadata" || !Array.isArray(fields) || fields.length === 0) return "";
  return `${fields.length} field${fields.length === 1 ? "" : "s"}: ${fields.map(humanizeFieldId).join(", ")}`;
};

const PageExtractorConfig = ({ config = {}, onChange }) => {
  const [expandedElements, setExpandedElements] = useState({});

  const urls = config.urls || [];
  const elements = config.elements || [];
  const steps = config.steps || [];
  const hasSteps = Array.isArray(steps) && steps.length > 0;

  const updateField = (field, value) => {
    onChange({ ...config, [field]: value });
  };

  // URL management - parse URLs from textarea (one per line)
  const handleUrlsChange = (e) => {
    const text = e.target.value;
    // Split by newlines and filter out empty lines
    const parsedUrls = text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    updateField("urls", parsedUrls);
  };

  // Convert URLs array to textarea value
  const urlsText = urls.join("\n");

  // Element management
  const addElement = () => {
    const newId = `element_${Date.now()}`;
    const newElement = {
      id: newId,
      name: `Element ${elements.length + 1}`,
      selectors: [],
    };
    updateField("elements", [...elements, newElement]);
    setExpandedElements({ ...expandedElements, [newId]: true });
  };

  const removeElement = (index) => {
    const updated = elements.filter((_, i) => i !== index);
    updateField("elements", updated);
  };

  const updateElement = (index, field, value) => {
    const updated = [...elements];
    updated[index] = { ...updated[index], [field]: value };
    updateField("elements", updated);
  };

  const toggleElement = (elementId) => {
    setExpandedElements({
      ...expandedElements,
      [elementId]: !expandedElements[elementId],
    });
  };

  // Steps-based (Page Extractor 2.0) element edits: write back the mutated element into the
  // matching element step, preserving the ordered steps[] shape on config.
  const updateStepElements = (stepIndex, updatedElements) => {
    const updatedSteps = steps.map((step, i) => (i === stepIndex ? { ...step, elements: updatedElements } : step));
    onChange({ ...config, steps: updatedSteps });
  };

  const updateStepElement = (stepIndex, elIndex, field, value) => {
    const stepElements = [...(steps[stepIndex]?.elements || [])];
    stepElements[elIndex] = { ...stepElements[elIndex], [field]: value };
    updateStepElements(stepIndex, stepElements);
  };

  // Shared element card renderer used by both the legacy flat editor and steps view.
  const renderElementCard = (element, index, onNameChange, onSelectorsChange, onRemove) => (
    <div
      key={element.id || index}
      className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/40 rounded-lg overflow-hidden"
    >
      <div
        className="flex items-center gap-2 p-2.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-white/[0.02] transition-colors"
        onClick={() => toggleElement(element.id || index)}
      >
        <span className="text-gray-400">
          {expandedElements[element.id || index] ? (
            <FiChevronDown className="w-4 h-4" />
          ) : (
            <FiChevronRight className="w-4 h-4" />
          )}
        </span>
        <input
          type="text"
          value={element.name || ""}
          onChange={(e) => {
            e.stopPropagation();
            onNameChange(e.target.value);
          }}
          onClick={(e) => e.stopPropagation()}
          placeholder="Element name"
          className="flex-1 px-2.5 py-1.5 bg-white dark:bg-gray-700/60 border border-gray-300 dark:border-gray-600/50
                     rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none
                     focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
        />
        <span className="text-2xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700/50 px-2 py-0.5 rounded-md">
          {(element.selectors || []).length} selector{(element.selectors || []).length !== 1 ? "s" : ""}
        </span>
        {onRemove && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="p-1.5 rounded-lg text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300
                       hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
          >
            <FiX className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {expandedElements[element.id || index] && (
        <div className="p-3 pt-0 border-t border-gray-200 dark:border-gray-700/30">
          <SelectorArrayEditor
            selectors={element.selectors || []}
            onChange={onSelectorsChange}
            label="Selectors"
            description="CSS or XPath selectors to find this element"
            emptyMessage="Add a selector to identify this element"
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-2.5">
      {/* URLs Section */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
          <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-500/15 mr-2">
            <FiLink className="text-blue-500 dark:text-blue-400 w-3 h-3" />
          </div>
          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">URLs to Extract</h3>
          <span className="ml-auto text-2xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700/50 px-2 py-0.5 rounded-md">
            {urls.length} URL{urls.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="space-y-2">
          <textarea
            value={urlsText}
            onChange={handleUrlsChange}
            placeholder="https://example.com/page1&#10;https://example.com/page2&#10;https://example.com/page3"
            rows={6}
            className="w-full bg-white dark:bg-gray-700/60 text-gray-900 dark:text-white px-3 py-2 rounded-lg text-xs
                       border border-gray-300 dark:border-gray-600/50 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none
                       placeholder-gray-400 dark:placeholder-gray-500 resize-none font-mono"
          />
          <p className="text-2xs text-gray-400 dark:text-gray-500">Enter one URL per line</p>
        </div>
      </div>

      {/* Steps view (Page Extractor 2.0): ordered element + action steps. */}
      {hasSteps ? (
        <div className="space-y-2.5">
          {steps.map((step, stepIndex) => {
            if (step?.kind === "action") {
              const meta = ACTION_META[step.actionId] || { emoji: "⚙️", label: step.actionId || "Action" };
              const optionSummary = describeStepOptions(step);
              return (
                <div
                  key={`step-${stepIndex}`}
                  className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm"
                >
                  <div className="flex items-center gap-2">
                    <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-blue-100 dark:bg-blue-500/15 text-base">
                      {meta.emoji}
                    </span>
                    <div className="min-w-0">
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">{meta.label}</span>
                      {optionSummary ? (
                        <p className="text-2xs text-sky-600 dark:text-sky-400 truncate" title={optionSummary}>
                          {optionSummary}
                        </p>
                      ) : (
                        <p className="text-2xs text-gray-400 dark:text-gray-500">Automatic action step</p>
                      )}
                    </div>
                    <span className="ml-auto text-2xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700/50 px-2 py-0.5 rounded-md">
                      Step {stepIndex + 1}
                    </span>
                  </div>
                </div>
              );
            }

            // Element step: reuse the element list UI to show/edit step.elements.
            const stepElements = step?.elements || [];
            return (
              <div
                key={`step-${stepIndex}`}
                className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm"
              >
                <div className="flex items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
                  <div className="p-1.5 rounded-lg bg-violet-100 dark:bg-violet-500/15 mr-2">
                    <FiBox className="text-violet-500 dark:text-violet-400 w-3 h-3" />
                  </div>
                  <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">Elements to Extract</h3>
                  <span className="ml-auto text-2xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700/50 px-2 py-0.5 rounded-md">
                    Step {stepIndex + 1}
                  </span>
                </div>

                <div className="space-y-2">
                  {stepElements.length === 0 ? (
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/30 border border-dashed border-gray-300 dark:border-gray-700/50 rounded-lg text-center">
                      <p className="text-xs text-gray-400 dark:text-gray-500">No elements in this step.</p>
                    </div>
                  ) : (
                    stepElements.map((element, elIndex) =>
                      renderElementCard(
                        element,
                        `${stepIndex}-${elIndex}`,
                        (name) => updateStepElement(stepIndex, elIndex, "name", name),
                        (selectors) => updateStepElement(stepIndex, elIndex, "selectors", selectors),
                        null,
                      )
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Legacy flat elements editor (extract_page_details). */
        <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
            <div className="p-1.5 rounded-lg bg-violet-100 dark:bg-violet-500/15 mr-2">
              <FiBox className="text-violet-500 dark:text-violet-400 w-3 h-3" />
            </div>
            <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">Elements to Extract</h3>
            <button
              onClick={addElement}
              className="ml-auto px-2 py-1 rounded-lg text-xs font-medium
                       bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400 border border-violet-200 dark:border-violet-500/30
                       hover:bg-violet-200 dark:hover:bg-violet-500/30 hover:border-violet-300 dark:hover:border-violet-400/50
                       transition-colors flex items-center gap-1"
            >
              <FiPlus className="w-3 h-3" />
              Add
            </button>
          </div>

          <p className="text-2xs text-gray-400 dark:text-gray-500 mb-3">Define what data to extract from each page</p>

          <div className="space-y-2">
            {elements.length === 0 ? (
              <div className="p-4 bg-gray-50 dark:bg-gray-800/30 border border-dashed border-gray-300 dark:border-gray-700/50 rounded-lg text-center">
                <p className="text-xs text-gray-400 dark:text-gray-500">No elements defined. Add an element to specify what to extract.</p>
              </div>
            ) : (
              elements.map((element, index) =>
                renderElementCard(
                  element,
                  index,
                  (name) => updateElement(index, "name", name),
                  (selectors) => updateElement(index, "selectors", selectors),
                  () => removeElement(index),
                )
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PageExtractorConfig;
